import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { checkoutOffers, requestPro } from '../../api/billing';
import { funnelStep, useFunnelStep } from '../../analytics/funnel';
import { trialCard } from '../../analytics/experiment';
import type { Offer } from '../../state/trial';
import {
  NO_CARD_REMINDER,
  PLANS,
  PRO,
  SUBSCRIBE_REMINDER,
  TRIAL_BUTTON,
  TRIAL_REMINDER,
  foundingBadge,
  foundingFor,
  noCardTerms,
  rememberFoundingChoice,
  priceLine,
  subscribeButton,
  subscribeTerms,
  trialEndFromNow,
  trialLine,
  trialTerms,
  type Founding,
  type PlanId,
} from '../../state/plans';
import { Button } from '../../ui';

/**
 * The plan choice (owner, 2026-10-27): Single, Monthly with its monthly/yearly switch, and Pro.
 *
 * Choosing a card selects it; there is ONE button, for the selected self-serve plan, and directly
 * under it the two lines the owner wrote — the reminder promise, then the full terms in small type
 * (what the auto-renewal rules ask to be said before the card is taken). Pro is not sold here: its
 * own button asks us to get in touch. Single is selected to begin with — the cheapest way to keep
 * the one site somebody has just made.
 *
 * On a phone the cards stack, and the button with its two lines sits right after the two plans it
 * sells, before Pro, so nobody hunts for the terms of what they are about to press.
 */
export default function PlanChoice({
  offer,
  trialEndsAt,
  busy,
  appName,
  continueLabel,
  notice,
  accessibilityLabel,
  onChoose,
}: {
  offer: Offer;
  /** ISO: when a trial taken now first charges — a free week's last day. Absent: today + 14 days. */
  trialEndsAt?: string;
  busy: boolean;
  /** The app on screen, for Pro's note to us. */
  appName?: string;
  /** The button's words once a warning has been said ("Continue to checkout"). */
  continueLabel?: string;
  /** Said above the button: why the last press stopped. */
  notice?: ReactNode;
  /** The button's accessible name from its words, where the caller's context adds to it. */
  accessibilityLabel?: (label: string) => string;
  onChoose: (plan: PlanId) => void;
}) {
  const group = useId();
  const [picked, setPicked] = useState<'single' | 'monthly'>('single');
  const [yearly, setYearly] = useState(false);
  const planId: PlanId = picked === 'single' ? 'single' : yearly ? 'monthly_annual' : 'monthly';
  const plan = PLANS[planId];
  const monthly = PLANS[yearly ? 'monthly_annual' : 'monthly'];
  const trial = offer === 'trial';
  // Today + 14 worked out once a showing, so the date said does not move under somebody reading it;
  // a free week's end, when the balance brings one, is said instead.
  const [fromNow] = useState(() => trialEndFromNow().toISOString());
  const chargeAt = trialEndsAt ?? fromNow;
  /*
   * The no-card test (owner, 2026-11-01, 50/50; analytics/experiment.ts `trial_card`): joined here, and
   * only for the full 14-day trial — a free week's remainder (trialEndsAt) always took the card. The
   * server reads the same group from the checkout request (api/billing.ts) and makes the trial to match.
   */
  const [card] = useState<'now' | 'later'>(() => (trial && !trialEndsAt ? trialCard() : 'now'));

  useFunnelStep('plan_choice_viewed', true, { offer });

  // The founding offer while any are left (2026-10-29): asked once a showing; no answer, no offer said.
  const [founding, setFounding] = useState<Founding | null>(null);
  /*
   * And the button waits for that answer (2026-11-01: a tap in the first second went to Stripe saying $10
   * here while Stripe charged the founding $5). At most 2.5 s: past that, what is said is the full price,
   * and the server, which applies the coupon, is the one that decides.
   */
  const [offersKnown, setOffersKnown] = useState(false);
  useEffect(() => {
    let live = true;
    const giveUp = setTimeout(() => { if (live) setOffersKnown(true); }, 2500);
    void checkoutOffers().then((o) => {
      if (!live) return;
      setFounding(o.founding ?? null);
      setOffersKnown(true);
    });
    return () => { live = false; clearTimeout(giveUp); };
  }, []);
  const chosen = useRef<PlanId | null>(null);

  const label = continueLabel ?? (trial ? TRIAL_BUTTON : subscribeButton(plan, founding));

  function choose(): void {
    // Once per plan per showing: the press after a warning is the same choice.
    if (chosen.current !== planId) funnelStep('plan_chosen', { plan: planId });
    chosen.current = planId;
    rememberFoundingChoice(foundingFor(plan, founding));
    onChoose(planId);
  }

  const [pro, setPro] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [proError, setProError] = useState<string | null>(null);
  async function talkToUs(): Promise<void> {
    if (pro !== 'idle') return;
    funnelStep('pro_pressed');
    setPro('sending');
    setProError(null);
    const result = await requestPro(appName);
    if (result.status === 'sent') {
      setPro('sent');
      return;
    }
    setPro('idle');
    setProError(result.status === 'signin_required' ? 'Sign in first, then press Talk to us again.' : result.message);
  }

  return (
    <div className="plan-choice">
      {founding && <p className="type-caption plan-founding">{foundingBadge(founding)}</p>}
      <div role="radiogroup" aria-label="Choose a plan" className="plan-cards">
        <label className={picked === 'single' ? 'plan-card is-selected' : 'plan-card'}>
          <input
            type="radio"
            name={group}
            className="plan-card-radio"
            checked={picked === 'single'}
            onChange={() => setPicked('single')}
            aria-label={`${PLANS.single.name}, ${priceLine(PLANS.single)}`}
          />
          <span className="plan-card-body">
            <span className="plan-card-head">
              <span className="type-heading plan-card-name">{PLANS.single.name}</span>
              <span className="type-body plan-card-price">{priceLine(PLANS.single)}</span>
            </span>
            <span className="type-caption plan-card-blurb">{PLANS.single.blurb}</span>
            {trial && <span className="type-caption plan-card-trial">{trialLine(PLANS.single, founding)}</span>}
          </span>
        </label>

        <div className={picked === 'monthly' ? 'plan-card is-selected' : 'plan-card'}>
          <label className="plan-card-row">
            <input
              type="radio"
              name={group}
              className="plan-card-radio"
              checked={picked === 'monthly'}
              onChange={() => setPicked('monthly')}
              aria-label={`${monthly.name}, ${priceLine(monthly)}`}
            />
            <span className="plan-card-body">
              <span className="plan-card-head">
                <span className="type-heading plan-card-name">{monthly.name}</span>
                <span className="type-body plan-card-price">{priceLine(monthly)}</span>
              </span>
              <span className="type-caption plan-card-blurb">{monthly.blurb}</span>
              {trial && <span className="type-caption plan-card-trial">{trialLine(monthly, founding)}</span>}
            </span>
          </label>
          {/* Paying by the month or the year: either selects this card too. */}
          <div className="plan-billing" role="group" aria-label="Pay for Monthly">
            <button
              type="button"
              className={yearly ? '' : 'is-selected'}
              aria-pressed={!yearly}
              onClick={() => { setYearly(false); setPicked('monthly'); }}
            >
              Monthly
            </button>
            <button
              type="button"
              className={yearly ? 'is-selected' : ''}
              aria-pressed={yearly}
              onClick={() => { setYearly(true); setPicked('monthly'); }}
            >
              Yearly
            </button>
          </div>
        </div>
      </div>

      {notice}

      <div className="plan-choice-go">
        <Button title={label} onPress={choose} loading={busy || !offersKnown} accessibilityLabel={accessibilityLabel ? accessibilityLabel(label) : label} />
        <p className="type-body plan-choice-reminder">{!trial ? SUBSCRIBE_REMINDER : card === 'later' ? NO_CARD_REMINDER : TRIAL_REMINDER}</p>
        <p className="type-caption plan-choice-terms">
          {!trial ? subscribeTerms(plan, founding) : card === 'later' ? noCardTerms(plan, chargeAt, founding) : trialTerms(plan, chargeAt, founding)}
        </p>
      </div>

      <div className="plan-card plan-card-pro">
        <span className="plan-card-head">
          <span className="type-heading plan-card-name">{PRO.name}</span>
          <span className="type-body plan-card-price">{PRO.price}</span>
        </span>
        <span className="type-caption plan-card-blurb">{PRO.blurb}</span>
        {pro === 'sent' ? (
          <p role="status" className="type-body plan-card-thanks">{PRO.thanks}</p>
        ) : (
          <Button title={PRO.button} variant="secondary" onPress={() => void talkToUs()} loading={pro === 'sending'} />
        )}
        {proError && <p role="alert" data-error-code="pro_request" className="type-caption field-error-text">{proError}</p>}
      </div>
    </div>
  );
}
